import {
  IonAvatar,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  useIonRouter,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import type { Employee } from "@/lib/backend/api";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { magnifyingGlassIcon, xMarkIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { useInfiniteEmployees } from "@/lib/query/app-user/use-infinite-employees";
import { useCreateConversation } from "@/lib/query/conversation/use-create-conversation";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";

interface NewChatModalProps {
  onClose: () => void;
}

export function NewChatModal({ onClose }: NewChatModalProps) {
  const { t } = useTranslation();
  const haptics = usePreferredHaptics();
  const router = useIonRouter();
  const [searchText, setSearchText] = useState("");
  const [internalSearch, setInternalSearch] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const { employees, loadMore, hasNextPage, isLoading } = useInfiniteEmployees(
    searchText || undefined,
  );

  const createConversation = useCreateConversation();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInternalSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchText(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleEmployeeSelect = async (employee: Employee) => {
    haptics.impact();

    try {
      const conversation = await createConversation.mutateAsync({
        type: "DIRECT",
        participantIds: [employee.id],
      });

      onClose();
      router.push(Routes.Conversation({ id: conversation.id }));
    } catch (error) {
      logger.error("Failed to create conversation:", error);
      toast.error(t("routes:conversations.create-failed"));
    }
  };

  const handleClose = () => {
    haptics.impact();
    onClose();
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-t-[10px] bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        {/* Empty spacer for centering */}
        <div className="size-6" />

        {/* Title */}
        <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">
          {t("routes:conversations.new-chat")}
        </h2>

        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="flex size-6 items-center justify-center text-txt-primary transition-opacity active:opacity-70"
        >
          <IonIcon icon={xMarkIcon} className="size-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-5 pt-0">
        {/* Search bar */}
        <div className="flex items-center gap-2.5 rounded-[10px] border-[1.25px] border-bordercolor-primary px-4 py-2.5">
          <IonIcon icon={magnifyingGlassIcon} className="size-5 shrink-0 text-txt-primary" />
          <input
            type="text"
            value={internalSearch}
            onChange={handleSearchChange}
            placeholder={t("routes:conversations.search-name-or-phone")}
            className="flex-1 bg-transparent text-[15px] font-medium leading-[1.6] text-txt-primary outline-none placeholder:text-txt-placeholder"
          />
        </div>

        {/* Employees list */}
        <div className="flex flex-col gap-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-txt-secondary">{t("common:loading")}</div>
            </div>
          ) : employees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-txt-secondary">
                {searchText
                  ? t("routes:conversations.no-contacts-found")
                  : t("routes:conversations.no-contacts")}
              </p>
            </div>
          ) : (
            employees.map((employee) => (
              <button
                key={employee.id}
                type="button"
                onClick={() => handleEmployeeSelect(employee)}
                disabled={createConversation.isPending}
                className="flex items-center gap-3 py-2 text-left transition-colors active:bg-gray-50 disabled:opacity-50"
              >
                {/* Avatar */}
                <IonAvatar className="size-12 shrink-0">
                  <img
                    src={employee.user?.imageUrl || employee.user?.image || EmptyAvatar}
                    alt={employee.user?.name || ""}
                    className="size-full rounded-full object-cover"
                  />
                </IonAvatar>

                {/* Info */}
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-base font-semibold leading-[1.5] tracking-[0.08px] text-txt-primary">
                    {employee.user?.name}
                  </span>
                  {employee.user?.email && (
                    <span className="truncate text-sm font-medium leading-[1.4] text-txt-tertiary">
                      {employee.user.email}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasNextPage}>
          <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
        </IonInfiniteScroll>
      </div>
    </div>
  );
}
